import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './components/ConfirmModal';

const formatDecimalHoursToHHMM = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

const parseFlexibleTimeToDecimalHours = (timeString) => {
  if (!timeString || !timeString.trim()) return 0;
  const trimmed = timeString.trim();
  if (trimmed.includes(',')) {
    const v = parseFloat(trimmed.replace(',', '.'));
    if (isNaN(v)) throw new Error('Invalid decimal format');
    return v;
  }
  if (trimmed.includes(':')) return parseHHMMToDecimalHours(trimmed);
  const v = parseFloat(trimmed);
  if (isNaN(v)) throw new Error('Invalid number format');
  return v;
};

export default function EventFormModal({
  isOpen, onClose, onSave, start, end, initialData, onDelete,
  currentView, modalPosition, onTimeChange, setModalPosition, getModalPositionStyle,
}) {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [hours, setHours] = useState('');
  const [lastChangedField, setLastChangedField] = useState(null);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.classList.add('modal-open');
      return () => document.body.classList.remove('modal-open');
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetForm = () => {
    setDate('');
    setStartTime('');
    setEndTime('');
    setHours('');
    setErrors({});
    setHasSubmitted(false);
    setHasChanges(false);
  };

  // Populate form from initialData (edit) or slot selection (new)
  useEffect(() => {
    if (initialData) {
      setDate(moment(initialData.start).format('YYYY-MM-DD'));
      setStartTime(moment(initialData.start).format('HH:mm'));
      setEndTime(moment(initialData.end).format('HH:mm'));
      const durationHours = moment(initialData.end).diff(moment(initialData.start)) / 3600000;
      setHours(formatDecimalHoursToHHMM(durationHours));
    } else if (start && end) {
      setDate(moment(start).format('YYYY-MM-DD'));
      setStartTime(moment(start).format('HH:mm'));
      setEndTime(moment(end).format('HH:mm'));
      const durationHours = moment(end).diff(moment(start)) / 3600000;
      setHours(formatDecimalHoursToHHMM(durationHours));
    }
  }, [start, end, initialData]);

  // Sync startTime/endTime -> hours
  useEffect(() => {
    if (lastChangedField === 'startTime' || lastChangedField === 'endTime') {
      if (startTime && endTime && date) {
        const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (e.isAfter(s)) {
          setHours(formatDecimalHoursToHHMM(e.diff(s, 'minutes') / 60));
          if (onTimeChange) {
            onTimeChange({ ...(initialData || {}), start: s.toDate(), end: e.toDate(), logId: initialData?.logId || null });
          }
        }
      }
    }
  }, [startTime, endTime, date]);

  // Escape key
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && isOpen) handleClose(); };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, hasChanges]);

  // Arrow key navigation: up/down nudges times, left/right shifts day (week view)
  useEffect(() => {
    if (!isOpen) return;

    const handleArrowKeys = (event) => {
      const tag = document.activeElement.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement.isContentEditable;
      if (isEditable) return;

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!s.isValid() || !e.isValid() || e.diff(s, 'minutes') <= 0) return;
        const delta = event.key === 'ArrowUp' ? -15 : 15;

        if (event.shiftKey) {
          const newEnd = e.clone().add(delta, 'minutes');
          if (newEnd.isSameOrBefore(s) || newEnd.diff(s, 'minutes') / 60 > 23) return;
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('endTime');
          if (onTimeChange) onTimeChange({ ...(initialData || {}), start: s.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
        } else {
          const newStart = s.clone().add(delta, 'minutes');
          const newEnd = e.clone().add(delta, 'minutes');
          if (newStart.isBefore(moment(date).startOf('day')) || newEnd.isAfter(moment(date).endOf('day').add(1, 'minute'))) return;
          setStartTime(newStart.format('HH:mm'));
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('startTime');
          if (onTimeChange) onTimeChange({ ...(initialData || {}), start: newStart.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
        }
      }

      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && currentView === 'week') {
        event.preventDefault();
        const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!s.isValid() || !e.isValid()) return;
        const dayOfWeek = s.day();
        if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
        if (event.key === 'ArrowRight' && dayOfWeek === 0) return;
        const delta = event.key === 'ArrowLeft' ? -1 : 1;
        const newStart = s.clone().add(delta, 'days');
        const newEnd = e.clone().add(delta, 'days');

        setDate(newStart.format('YYYY-MM-DD'));
        setStartTime(newStart.format('HH:mm'));
        setEndTime(newEnd.format('HH:mm'));
        setLastChangedField('startTime');
        setModalPosition(getModalPositionStyle(newStart.toDate(), currentView));
        if (onTimeChange) onTimeChange({ ...(initialData || {}), start: newStart.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [isOpen, date, startTime, endTime, onTimeChange, initialData, currentView, setModalPosition, getModalPositionStyle]);

  // Track changes (times/date vs the initial slot or record)
  useEffect(() => {
    const ref = initialData || (start && end ? { start, end } : null);
    if (!ref) { setHasChanges(false); return; }
    const rDate = moment(ref.start).format('YYYY-MM-DD');
    const rStart = moment(ref.start).format('HH:mm');
    const rEnd = moment(ref.end).format('HH:mm');
    setHasChanges(date !== rDate || startTime !== rStart || endTime !== rEnd);
  }, [date, startTime, endTime, initialData, start, end]);

  const validateForm = () => {
    const newErrors = {};
    if (!date) newErrors.date = 'Please select a date';
    if (!startTime) newErrors.startTime = 'Please enter a start time';
    if (!hours) newErrors.hours = 'Please enter hours';
    else {
      const hhmmPattern = /^\d{1,2}:\d{2}$/;
      const decimalPattern = /^\d*[,.]?\d+$/;
      if (!hhmmPattern.test(hours) && !decimalPattern.test(hours)) {
        newErrors.hours = 'Use format like 1:30, 0,1 or 1.5';
      } else {
        try {
          const n = parseFlexibleTimeToDecimalHours(hours);
          if (n <= 0) newErrors.hours = 'Hours must be greater than zero';
          if (n > 23) newErrors.hours = 'Hours must be less than 23';
        } catch {
          newErrors.hours = 'Invalid time format';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    if (!validateForm()) return;

    const startDate = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
    let floatHours;
    try {
      floatHours = parseFlexibleTimeToDecimalHours(hours);
    } catch {
      setErrors(prev => ({ ...prev, hours: 'Invalid time format' }));
      return;
    }
    if (isNaN(floatHours) || floatHours <= 0) {
      setErrors(prev => ({ ...prev, hours: 'Invalid hours entered' }));
      return;
    }

    const endDate = new Date(startDate.getTime() + floatHours * 3600000);

    onSave({
      start: startDate,
      end: endDate,
      logId: initialData?.logId || null,
    });

    setHasSubmitted(false);
    resetForm();
    onClose();
  };

  const handleDelete = () => setShowDeleteConfirmModal(true);

  const handleConfirmDelete = () => {
    onDelete(initialData?.logId);
    resetForm();
    onClose();
    setShowDeleteConfirmModal(false);
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmModal(true);
    } else {
      resetForm();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const mobileStyles = {
    position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
    zIndex: 2600, bottom: '20px', display: 'flex', flexDirection: 'column',
  };
  const finalModalPosition = isMobile ? mobileStyles : modalPosition;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 md:bg-transparent"
        style={{ zIndex: 2500 }}
        onClick={handleClose}
      />
      <div
        className="bg-white rounded-lg w-full max-w-[88vw] md:w-[400px] h-auto max-h-full md:max-h-[90vh] shadow-xl border border-gray-300 modal-mobile flex flex-col"
        style={finalModalPosition}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3 p-3 md:p-6 pb-0 md:pb-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {initialData ? 'Edit' : 'New entry'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 p-1 transition-colors" aria-label="Close">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-3 md:p-6 pt-0 md:pt-0">

          {/* Date + Hours */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Date:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
              />
              {hasSubmitted && errors.date && <div className="text-red-500 text-sm mt-1">{errors.date}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Hours:</label>
              <input
                type="text"
                value={hours}
                onChange={(e) => {
                  setHours(e.target.value.replace(/[^0-9:,.]/g, ''));
                  setLastChangedField('hours');
                }}
                onBlur={() => {
                  try {
                    const parsed = parseFlexibleTimeToDecimalHours(hours);
                    if (!isNaN(parsed) && parsed > 0 && parsed <= 23) {
                      const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const newEnd = s.clone().add(parsed, 'hours');
                      setEndTime(newEnd.format('HH:mm'));
                      if (onTimeChange) onTimeChange({ ...(initialData || {}), start: s.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
                    } else {
                      const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                      if (s.isValid() && e.isValid() && e.isAfter(s)) {
                        setHours(formatDecimalHoursToHHMM(e.diff(s, 'minutes') / 60));
                      } else setHours('');
                    }
                  } catch {
                    setHours('');
                  }
                }}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
                placeholder="E.g. 1:30 or 0,1 or 1.5"
              />
              {hasSubmitted && errors.hours && <div className="text-red-500 text-sm mt-1">{errors.hours}</div>}
            </div>
          </div>

          {/* Start + End Time */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Start Time:</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => { setStartTime(e.target.value); setLastChangedField('startTime'); }}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
              />
              {hasSubmitted && errors.startTime && <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">End Time:</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => { setEndTime(e.target.value); setLastChangedField('endTime'); }}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
              />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="border-t border-gray-200 p-3 md:p-6 bg-white rounded-b-lg">
          <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
            <div className="order-2 md:order-1">
              {initialData && (
                <button
                  onClick={handleDelete}
                  className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 touch-manipulation font-medium"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="order-1 md:order-2">
              <button
                onClick={handleSubmit}
                className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-[#205799] text-white rounded hover:bg-[#273C61] touch-manipulation font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirmModal(false)}
        title="Unsaved Changes"
        message="Are you sure you want to discard your changes?"
        confirmText="Discard"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirmModal(false)}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}
